import { cloneDeep } from "lodash";
import { v4 as uuid } from "uuid";
import { ReportScopeActions } from "./report-scope-actions.js";

export const REPORT_SCOPE_MODES = {
	normal: "normal",
	minimal: "minimal"
};

class ScopedEventBus {
	constructor(instanceId, eventBus) {
		this.instanceId = instanceId;
		this.global = eventBus;
	}

	register(event, callback, scope, options) {
		return this.global.register(this.scopeEvent(event), callback, scope, options);
	}

	trigger(event, payload) {
		return this.global.trigger(this.scopeEvent(event), payload);
	}

	unregister(unbinder) {
		return this.global.unregister(unbinder);
	}

	scopeEvent(event) {
		return `/report/${this.instanceId}/${event}`;
	}
}

class ReportScope {
	constructor({
		eventBus,
		reportHelper,
		validationServiceFactory,
		form,
		report,
		mode,
		isIsolated,
		isReadOnly = false
	}) {
		this.$instance = uuid();
		this.mode = mode || REPORT_SCOPE_MODES.normal;
		this.isIsolated = isIsolated;
		this.isReadOnly = isReadOnly;
		this.events = new ScopedEventBus(this.$instance, eventBus);
		this.actions = new ReportScopeActions(this, reportHelper);
		this.validationService = validationServiceFactory.getNewValidationService();
		this.refreshForm({ form });
		this.handleReportUpdated(report);
	}

	refreshForm({ form } = {}) {
		this.form = cloneDeep(form);

		return this;
	}

	handleReportUpdated(report) {
		this.report = report;
		this.isNew = !report || report.Id === undefined;

		return this;
	}
}

class ReportScopeFactory {
	constructor(eventBus, reportHelper, validationServiceFactory) {
		this.eventBus = eventBus;
		this.reportHelper = reportHelper;
		this.validationServiceFactory = validationServiceFactory;
	}

	create({
		form,
		report,
		mode = REPORT_SCOPE_MODES.normal,
		isIsolated = true,
		isReadOnly = false
	} = {}) {
		return new ReportScope({
			eventBus: this.eventBus,
			reportHelper: this.reportHelper,
			validationServiceFactory: this.validationServiceFactory,
			form,
			report,
			mode,
			isIsolated,
			isReadOnly
		});
	}
}

class ReportScopeController {
	$onInit() {
		if(!this.reportScope) {
			throw new Error("report-scope requires a reportScope binding.");
		}
	}

	get $instance() {
		return this.reportScope.$instance;
	}

	get mode() {
		return this.reportScope?.mode;
	}

	get form() {
		return this.reportScope?.form;
	}

	get report() {
		return this.reportScope?.report;
	}

	get isNew() {
		return this.reportScope?.isNew;
	}

	get isIsolated() {
		return this.reportScope?.isIsolated;
	}

	get isReadOnly() {
		return this.reportScope?.isReadOnly;
	}

	get events() {
		return this.reportScope?.events;
	}

	get actions() {
		return this.reportScope?.actions;
	}

	get validationService() {
		return this.reportScope?.validationService;
	}

	refreshForm(options) {
		return this.reportScope.refreshForm(options);
	}

	handleReportUpdated(report) {
		return this.reportScope.handleReportUpdated(report);
	}
}

export default angular
	.module("vesl.components.report-scope", [])
	.service("reportScopeFactory", ReportScopeFactory)
	.directive("reportScope", () => ({
		restrict: "A",
		controller: ReportScopeController,
		controllerAs: "$reportScope",
		bindToController: {
			reportScope: "<"
		}
	})).name;
